import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
  useSearchParams,
  type RouteObject,
} from 'react-router-dom'
import { Suspense, useMemo } from 'react'
import { recoverableLazy } from '@/components/ModuleRecovery/ModuleRecovery'
import { RouteErrorPage } from '@/components/ModuleRecovery/RouteErrorPage'
import { MarketingLayout } from '@/pages/MarketingPages/MarketingLayout'
import { MainLayout } from '@/layouts/MainLayout'
import { AppLayout } from '@/layouts/AppLayout'
import { CampanhasLayout } from '@/layouts/CampanhasLayout'
import {
  AdminSensitiveTotpRoute,
  CampaignTotpStepUpGate,
} from '@/components/CampaignTotpStepUp'
import { AfiliadosTabsLayout } from '@/layouts/AfiliadosTabsLayout'
import { DistribuidoresTabsLayout } from '@/layouts/DistribuidoresTabsLayout'
import { SouAfiliadoTabsLayout } from '@/layouts/SouAfiliadoTabsLayout'
import { SouDistribuidorTabsLayout } from '@/layouts/SouDistribuidorTabsLayout'
import { SouDistribuidorAfiliadosTabsLayout } from '@/layouts/SouDistribuidorAfiliadosTabsLayout'
import { ClientesTabsLayout } from '@/layouts/ClientesTabsLayout'
import { MetaConversionsLayout } from '@/layouts/MetaConversionsLayout'
import { TabsLayout } from '@/layouts/TabsLayout'
import { AuthWatcherLayout } from '@/layouts/AuthWatcherLayout'
import { AdminOnlyRoute } from '@/components/ProtectedRoute/AdminOnlyRoute'
import { InstitutionOnlyRoute } from '@/components/ProtectedRoute/InstitutionOnlyRoute'
import { SharedManagerialDashboardRoute } from '@/components/ProtectedRoute/SharedManagerialDashboardRoute'
import { IndexPage } from '@/pages/IndexPage'
import { HomePage } from '@/pages/HomePage'
import { AdminHomePage } from '@/pages/AdminHomePage'
import { LoginPage } from '@/pages/LoginPage'
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage'
import { ResetPasswordPage } from '@/pages/ResetPasswordPage'
import { AdminLoginPage } from '@/pages/AdminLoginPage'
import { CadastroPage } from '@/pages/CadastroPage'
import { GatewayPagamentoPage } from '@/pages/GatewayPagamentoPage'
import {
  AfiliadosDashboardPage,
  AfiliadosListaPage,
  AfiliadosRelatorioPage,
  AfiliadosSaldoPage,
} from '@/pages/AfiliadosPage'
import {
  SouAfiliadoDashboardPage,
  SouAfiliadoLinksPage,
  SouAfiliadoSaldoPage,
  SouAfiliadoVendasPage,
} from '@/pages/SouAfiliadoPage'
import {
  DistribuidoresDashboardPage,
  DistribuidoresListaPage,
  DistribuidoresRelatorioPage,
  DistribuidoresSaldoPage,
} from '@/pages/DistribuidoresPage'
import {
  SouDistribuidorDashboardPage,
  SouDistribuidorSaldoPage,
  SouDistribuidorAfiliadosListaPage,
  SouDistribuidorAfiliadosLinksPage,
  SouDistribuidorAfiliadosVendasPage,
  SouDistribuidorAfiliadosSaldoPage,
} from '@/pages/SouDistribuidorPage'
import { CampanhasTab } from '@/pages/CampanhasPage/CampanhasTab'
import { CampanhaEditPage } from '@/pages/CampanhasPage/CampanhaEditPage'
import { DashboardTab } from '@/pages/CampanhasPage/DashboardTab'
import { ConfiguracoesTab } from '@/pages/CampanhasPage/ConfiguracoesTab'
import { RoletaTab } from '@/pages/CampanhasPage/RoletaTab'
import { ConfiguracoesPage } from '@/pages/ConfiguracoesPage'
import { CollaboratorsPage } from '@/pages/CollaboratorsPage'
import { CampanhaRaspadinhaLayout } from '@/layouts/CampanhaRaspadinhaLayout'
import {
  CampanhaRaspadinhaDashboardTab,
  CampanhaRaspadinhaEditPage,
  CampanhaRaspadinhaTab,
} from '@/pages/CampanhaRaspadinhaPage'
import { SplitsPadraoTab } from '@/pages/SplitsGlobaisPage/SplitsPadraoTab'
import { SplitsPersonalizadoTab } from '@/pages/SplitsGlobaisPage/SplitsPersonalizadoTab'
import { PerfilTab } from '@/pages/PerfilPage/PerfilTab'
import { AiAdminPage } from '@/features/campaignAi/AiSettings'
import { SegurancaTab } from '@/pages/PerfilPage/SegurancaTab'
import { NotFoundPage } from '@/pages/NotFoundPage'
import { LandingPage } from '@/pages/LandingPage/LandingPage'
import { LandingAccountPage } from '@/pages/LandingAccountPage/LandingAccountPage'
import { CampaignWhatsAppSlugRedirect } from '@/components/Landing/CampaignWhatsAppSlugRedirect'
import { LegacyLandingCheckoutRedirect } from '@/components/Landing/LegacyLandingCheckoutRedirect'
import { LandingNumbersPage } from '@/pages/LandingNumbersPage/LandingNumbersPage'
import { LandingObrigadoPage } from '@/pages/LandingObrigadoPage/LandingObrigadoPage'
import { ChecklistPage } from '@/pages/ChecklistPage'
import { DashGerencialPage } from '@/pages/DashGerencialPage'
import { CustosOperacionaisPage } from '@/pages/CustosOperacionaisPage'
import { MonitoramentoPage } from '@/pages/MonitoramentoPage'
import {
  LegalDocumentAcceptancesPage,
  LegalDocumentTabPage,
} from '@/pages/LegalDocumentsPage'
import { LEGAL_POLICIES_ADMIN_TABS } from '@/types/legal-documents'
import { EditarClientePage } from '@/pages/EditarClientePage'
import { ClientesListaPage } from '@/pages/ClientesPage'
import { PedidosLayout } from '@/layouts/PedidosLayout'
import { PedidoDetalhePage, PedidosListaPage } from '@/pages/PedidosPage'
import { WinnerLookupLayout } from '@/layouts/WinnerLookupLayout'
import { WinnerLookupPage } from '@/pages/WinnerLookupPage'
import { CampaignWinnerPublicPage } from '@/pages/CampaignWinnerPublicPage'
import {
  CustomDomainCampaignProvider,
  useCustomDomainCampaign,
} from '@/contexts/CustomDomainCampaignContext'
import {
  CustomDomainBootScreen,
  CustomDomainRootRedirect,
  CustomDomainSlugGate,
} from '@/components/CustomDomainCampaignGate'
import { RaspadinhaPublicPage } from '@/pages/RaspadinhaPublicPage'
import { IntegrationDocPage } from '@/pages/IntegrationDocPage'
import {
  MetaConversionsDashboardPage,
  MetaConversionsEventsPage,
} from '@/pages/MetaConversionsPage'

const AdminAvatarPage = recoverableLazy(async () => {
  const module = await import('@/pages/CampanhasPage/AvatarTab')
  return { default: module.AdminAvatarPage }
})
const ShopPage = recoverableLazy(async () => {
  const module = await import('@/features/shop/ShopPage')
  return { default: module.ShopPage }
})

const ResourceHubPage = recoverableLazy(async () => {
  const module = await import('@/pages/MarketingPages/ResourcePages')
  return { default: module.ResourceHubPage }
})
const ResourceDetailPage = recoverableLazy(async () => {
  const module = await import('@/pages/MarketingPages/ResourcePages')
  return { default: module.ResourceDetailPage }
})
const CampaignGuidePage = recoverableLazy(async () => {
  const module = await import('@/pages/MarketingPages/ResourcePages')
  return { default: module.CampaignGuidePage }
})

function LegacyCampaignGamificationRedirect() {
  const [searchParams] = useSearchParams()
  const campaignId = searchParams.get('id')

  return (
    <Navigate
      to={
        campaignId && /^[1-9]\d*$/.test(campaignId)
          ? `/campanhas/editar?id=${campaignId}#gamificacao`
          : '/campanhas'
      }
      replace
    />
  )
}

function createResilientRouter(routes: RouteObject[]) {
  return createBrowserRouter(
    routes.map((route) => ({ ...route, errorElement: <RouteErrorPage /> }))
  )
}

const router = createResilientRouter([
  { path: '/conta', element: <LandingAccountPage /> },
  { path: '/conta/redefinir-senha', element: <LandingAccountPage /> },
  {
    path: '/recursos',
    element: <MarketingLayout />,
    children: [
      { index: true, element: <ResourceHubPage /> },
      { path: ':slug', element: <ResourceDetailPage /> },
    ],
  },
  {
    path: '/como-funciona',
    element: <MarketingLayout />,
    children: [{ index: true, element: <CampaignGuidePage /> }],
  },
  { path: '/doc', element: <IntegrationDocPage /> },
  {
    path: '/campanha/:slug',
    element: <CampaignWhatsAppSlugRedirect />,
    children: [
      { path: 'conta', element: <LandingAccountPage /> },
      { path: 'checkout', element: <LegacyLandingCheckoutRedirect /> },
      { path: 'perfil', element: <LandingNumbersPage /> },
      { path: 'ganhador', element: <CampaignWinnerPublicPage /> },
      { path: 'obrigado', element: <LandingObrigadoPage /> },
      { index: true, element: <LandingPage /> },
    ],
  },
  {
    path: '/raspadinha/:slug',
    element: <RaspadinhaPublicPage />,
  },
  {
    element: <AuthWatcherLayout />,
    children: [
      {
        path: '/',
        element: <MainLayout />,
        children: [{ index: true, element: <IndexPage /> }],
      },
      {
        path: 'home',
        element: <AppLayout />,
        children: [{ index: true, element: <HomePage /> }],
      },
      {
        path: 'home-drftadmin',
        element: <AppLayout />,
        children: [{ index: true, element: <AdminHomePage /> }],
      },
      {
        path: 'campanhas',
        element: <AppLayout />,
        children: [
          {
            element: <CampanhasLayout />,
            children: [
              { index: true, element: <CampanhasTab /> },
              {
                path: 'criar',
                element: <Navigate to="/campanhas?modal=create" replace />,
              },
              {
                path: 'editar',
                element: (
                  <CampaignTotpStepUpGate fallbackTo="/campanhas">
                    <CampanhaEditPage />
                  </CampaignTotpStepUpGate>
                ),
              },
              { path: 'dashboard', element: <DashboardTab /> },
              {
                path: 'gamificacao',
                element: <LegacyCampaignGamificationRedirect />,
              },
              { path: 'configuracoes', element: <ConfiguracoesTab /> },
              {
                path: 'roleta',
                element: (
                  <CampaignTotpStepUpGate fallbackTo="/campanhas">
                    <RoletaTab />
                  </CampaignTotpStepUpGate>
                ),
              },
            ],
          },
        ],
      },
      {
        path: 'gateway-pagamento',
        element: <AppLayout />,
        children: [
          { index: true, element: <GatewayPagamentoPage /> },
          {
            path: 'configuracoes',
            element: <Navigate to="/gateway-pagamento" replace />,
          },
        ],
      },
      {
        path: 'shop',
        element: <AppLayout />,
        children: [{ index: true, element: <Suspense fallback={<p role="status">Carregando Shop…</p>}><ShopPage /></Suspense> }],
      },
      {
        path: 'afiliados',
        element: <AppLayout />,
        children: [
          {
            element: <AfiliadosTabsLayout />,
            children: [
              { index: true, element: <AfiliadosDashboardPage /> },
              { path: 'lista', element: <AfiliadosListaPage /> },
              { path: 'saldo', element: <AfiliadosSaldoPage /> },
              { path: 'relatorio', element: <AfiliadosRelatorioPage /> },
            ],
          },
        ],
      },
      {
        path: 'sou-afiliado',
        element: <AppLayout />,
        children: [
          {
            element: <SouAfiliadoTabsLayout />,
            children: [
              { index: true, element: <SouAfiliadoDashboardPage /> },
              { path: 'links', element: <SouAfiliadoLinksPage /> },
              { path: 'vendas', element: <SouAfiliadoVendasPage /> },
              { path: 'saldo', element: <SouAfiliadoSaldoPage /> },
            ],
          },
        ],
      },
      {
        path: 'meus-distribuidores',
        element: <AppLayout />,
        children: [
          {
            element: <DistribuidoresTabsLayout />,
            children: [
              { index: true, element: <DistribuidoresDashboardPage /> },
              { path: 'lista', element: <DistribuidoresListaPage /> },
              { path: 'saldo', element: <DistribuidoresSaldoPage /> },
              { path: 'relatorio', element: <DistribuidoresRelatorioPage /> },
            ],
          },
        ],
      },
      {
        path: 'sou-distribuidor',
        element: <AppLayout />,
        children: [
          {
            element: <SouDistribuidorTabsLayout />,
            children: [
              { index: true, element: <SouDistribuidorDashboardPage /> },
              { path: 'saldo', element: <SouDistribuidorSaldoPage /> },
              {
                path: 'afiliados',
                element: <SouDistribuidorAfiliadosTabsLayout />,
                children: [
                  {
                    index: true,
                    element: <Navigate to="/sou-distribuidor/afiliados/lista" replace />,
                  },
                  { path: 'lista', element: <SouDistribuidorAfiliadosListaPage /> },
                  { path: 'links', element: <SouDistribuidorAfiliadosLinksPage /> },
                  { path: 'vendas', element: <SouDistribuidorAfiliadosVendasPage /> },
                  { path: 'saldo', element: <SouDistribuidorAfiliadosSaldoPage /> },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'clientes',
        element: <AppLayout />,
        children: [
          {
            element: <ClientesTabsLayout />,
            children: [
              { index: true, element: <Navigate to="/clientes/lista" replace /> },
              { path: 'lista', element: <ClientesListaPage /> },
            ],
          },
        ],
      },
      {
        path: 'clientes/:id/editar',
        element: <AppLayout />,
        children: [{ index: true, element: <EditarClientePage /> }],
      },
      {
        path: 'pedidos',
        element: <AppLayout />,
        children: [
          {
            element: <PedidosLayout />,
            children: [
              { index: true, element: <Navigate to="/pedidos/lista" replace /> },
              { path: 'lista', element: <PedidosListaPage /> },
              { path: 'lista/pedido/:paymentId', element: <PedidoDetalhePage /> },
            ],
          },
        ],
      },
      {
        path: 'conversoes-meta',
        element: <AppLayout />,
        children: [
          {
            element: <MetaConversionsLayout />,
            children: [
              { index: true, element: <MetaConversionsDashboardPage channel="meta" /> },
              { path: 'google', element: <MetaConversionsDashboardPage channel="ga4" /> },
              {
                path: 'eventos',
                element: <Navigate to="/conversoes-meta/eventos/meta" replace />,
              },
              {
                path: 'eventos/meta',
                element: <MetaConversionsEventsPage channel="meta" />,
              },
              {
                path: 'eventos/google',
                element: <MetaConversionsEventsPage channel="ga4" />,
              },
            ],
          },
        ],
      },
      {
        path: 'buscar-ganhador',
        element: <AppLayout />,
        children: [
          {
            element: <WinnerLookupLayout />,
            children: [{ index: true, element: <WinnerLookupPage /> }],
          },
        ],
      },
      {
        path: 'colaboradores',
        element: <AppLayout />,
        children: [{ index: true, element: <CollaboratorsPage /> }],
      },
      {
        path: 'campanha-raspadinha',
        element: <AppLayout />,
        children: [
          {
            element: (
              <InstitutionOnlyRoute>
                <CampanhaRaspadinhaLayout />
              </InstitutionOnlyRoute>
            ),
            children: [
              { index: true, element: <CampanhaRaspadinhaTab /> },
              {
                path: 'criar',
                element: <Navigate to="/campanha-raspadinha?modal=create" replace />,
              },
              {
                path: 'editar',
                element: (
                  <CampaignTotpStepUpGate fallbackTo="/campanha-raspadinha">
                    <CampanhaRaspadinhaEditPage />
                  </CampaignTotpStepUpGate>
                ),
              },
              { path: 'dashboard', element: <CampanhaRaspadinhaDashboardTab /> },
            ],
          },
        ],
      },
      {
        path: 'configuracoes',
        element: <AppLayout />,
        children: [{ index: true, element: <ConfiguracoesPage /> }],
      },
      {
        path: 'dash-gerencial',
        element: <AppLayout />,
        children: [
          {
            index: true,
            element: (
              <AdminOnlyRoute>
                <DashGerencialPage />
              </AdminOnlyRoute>
            ),
          },
        ],
      },
      {
        path: 'custos-operacionais',
        element: <AppLayout />,
        children: [
          {
            index: true,
            element: (
              <AdminSensitiveTotpRoute>
                <CustosOperacionaisPage />
              </AdminSensitiveTotpRoute>
            ),
          },
        ],
      },
      {
        path: 'dashboard-empresarial',
        element: <AppLayout />,
        children: [
          {
            index: true,
            element: (
              <SharedManagerialDashboardRoute>
                <DashGerencialPage />
              </SharedManagerialDashboardRoute>
            ),
          },
        ],
      },
      {
        path: 'checklist',
        element: <AppLayout />,
        children: [
          {
            index: true,
            element: (
              <AdminOnlyRoute>
                <ChecklistPage />
              </AdminOnlyRoute>
            ),
          },
        ],
      },
      {
        path: 'monitoramento',
        element: <AppLayout />,
        children: [
          {
            index: true,
            element: (
              <AdminSensitiveTotpRoute forceRefresh>
                <MonitoramentoPage />
              </AdminSensitiveTotpRoute>
            ),
          },
        ],
      },
      {
        path: 'termos-politicas',
        element: <AppLayout />,
        children: [
          {
            element: (
              <AdminSensitiveTotpRoute>
                <TabsLayout
                  title="Termos e Políticas"
                  subtitle="Gerencie versões dos documentos legais exibidos no aceite de instituições e colaboradores."
                  tabs={[...LEGAL_POLICIES_ADMIN_TABS]}
                />
              </AdminSensitiveTotpRoute>
            ),
            children: [
              {
                index: true,
                element: <LegalDocumentTabPage documentType="platform_terms" />,
              },
              {
                path: 'termos-de-uso',
                element: <LegalDocumentTabPage documentType="terms_of_use" />,
              },
              {
                path: 'privacidade',
                element: <LegalDocumentTabPage documentType="privacy_policy" />,
              },
              {
                path: 'cookies',
                element: <LegalDocumentTabPage documentType="cookie_policy" />,
              },
              {
                path: 'aceites',
                element: <LegalDocumentAcceptancesPage />,
              },
            ],
          },
        ],
      },
      {
        path: 'inteligencia-artificial',
        element: <AppLayout />,
        children: [
          {
            index: true,
            element: (
              <AdminSensitiveTotpRoute forceRefresh>
                <AiAdminPage />
              </AdminSensitiveTotpRoute>
            ),
          },
        ],
      },
      {
        path: 'avatar-adm',
        element: <AppLayout />,
        children: [
          {
            index: true,
            element: (
              <AdminSensitiveTotpRoute>
                <Suspense
                  fallback={<p role="status">Abrindo ateliê global de avatar…</p>}
                >
                  <AdminAvatarPage />
                </Suspense>
              </AdminSensitiveTotpRoute>
            ),
          },
        ],
      },
      {
        path: 'splits-globais',
        element: <AppLayout />,
        children: [
          {
            element: (
              <AdminSensitiveTotpRoute>
                <TabsLayout
                  title="Splits Globais"
                  subtitle="Configure e gerencie a distribuição global de splits entre plataforma e instituições."
                  tabs={[
                    { value: 'padrao', label: 'Padrão', to: '/splits-globais' },
                    {
                      value: 'personalizado',
                      label: 'Personalizado',
                      to: '/splits-globais/personalizado',
                    },
                    {
                      value: 'avatar-mascote',
                      label: 'Split padrão avatar/mascote',
                      to: '/splits-globais/avatar-mascote',
                    },
                    {
                      value: 'avatar-adm',
                      label: 'AVATAR ADM',
                      to: '/splits-globais/avatar-adm',
                    },
                  ]}
                />
              </AdminSensitiveTotpRoute>
            ),
            children: [
              { index: true, element: <SplitsPadraoTab /> },
              { path: 'personalizado', element: <SplitsPersonalizadoTab /> },
              {
                path: 'avatar-adm',
                element: <SplitsPadraoTab key="avatar-adm" category="avatar_admin" />,
              },
              {
                path: 'avatar-mascote',
                element: (
                  <SplitsPadraoTab key="avatar-mascote" category="avatar_mascot" />
                ),
              },
            ],
          },
        ],
      },
      {
        path: 'perfil',
        element: <AppLayout />,
        children: [
          {
            element: (
              <TabsLayout
                title="Meu perfil"
                subtitle="Atualize suas informa??es pessoais"
                tabs={[
                  {
                    value: 'perfil',
                    label: 'Perfil',
                    icon: 'mdi:account',
                    to: '/perfil',
                  },
                  {
                    value: 'seguranca',
                    label: 'Segurança',
                    icon: 'mdi:shield-lock',
                    to: '/perfil/seguranca',
                  },
                ]}
              />
            ),
            children: [
              { index: true, element: <PerfilTab /> },
              { path: 'seguranca', element: <SegurancaTab /> },
            ],
          },
        ],
      },
      { path: '/login', element: <LoginPage /> },
      { path: '/recuperar-senha', element: <ForgotPasswordPage /> },
      { path: '/redefinir-senha', element: <ResetPasswordPage /> },
      { path: '/drftadmin', element: <AdminLoginPage /> },
      { path: '/cadastro', element: <CadastroPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
])

function createCustomDomainRouter(slug: string) {
  return createResilientRouter([
    {
      path: '/',
      element: <CustomDomainRootRedirect slug={slug} />,
    },
    {
      path: '/campanha/:slug',
      element: <CustomDomainSlugGate expectedSlug={slug} />,
      children: [
        { index: true, element: <LandingPage /> },
        { path: 'conta', element: <LandingAccountPage /> },
        { path: 'checkout', element: <LegacyLandingCheckoutRedirect /> },
        { path: 'perfil', element: <LandingNumbersPage /> },
        { path: 'ganhador', element: <CampaignWinnerPublicPage /> },
        { path: 'obrigado', element: <LandingObrigadoPage /> },
        { path: '*', element: <CustomDomainRootRedirect slug={slug} /> },
      ],
    },
    {
      path: '*',
      element: <CustomDomainRootRedirect slug={slug} />,
    },
  ])
}

function createUnknownHostRouter() {
  return createResilientRouter([
    {
      path: '*',
      element: <NotFoundPage />,
    },
  ])
}

const unknownHostRouter = createUnknownHostRouter()

function AppRouterInner() {
  const { status, slug } = useCustomDomainCampaign()
  const customRouter = useMemo(
    () => (status === 'custom' && slug ? createCustomDomainRouter(slug) : null),
    [status, slug]
  )

  if (status === 'loading') {
    return <CustomDomainBootScreen />
  }

  if (status === 'unknown') {
    return <RouterProvider router={unknownHostRouter} />
  }

  if (customRouter) {
    return <RouterProvider router={customRouter} />
  }

  return <RouterProvider router={router} />
}

export function AppRouter() {
  return (
    <CustomDomainCampaignProvider>
      <AppRouterInner />
    </CustomDomainCampaignProvider>
  )
}
